import { useDispatch, useSelector } from "react-redux";
import { setActiveMarkerId, setEditPlaceId, setHighlightedPlaceId, setShowEditPlaceModal, setShowViewPlaceModal, setViewPlaceId } from "../../../store";
import { useEffect, useRef, useState } from "react";
import { COLOR_BLACK, COLOR_PRIMARY, COLOR_WHITE } from "../../../utils/styles";
import { Box } from "@mui/material";
import { convertHoursOfOperation, getDayOfTheWeek, isOpenNow } from "../../../utils/dataFormatter";
import { IconButton, Stack, Typography } from "@mui/joy";
import EditIcon from '@mui/icons-material/Edit';
import OpenNow from "../../common/OpenNow";
import { useNavigate } from "react-router-dom";

function MapMarker(props) {
  const navigate = useNavigate();
  const username = useSelector((state) => state.user.profile.username);
  const myElement = useRef(null);
  const dispatch = useDispatch();
  const activeMarkerId = useSelector((state) => state.map.activeMarkerId);
  const filterAcceptsReservations = useSelector(
    (state) => state.filter.acceptsReservations,
  );
  const filterHasWineGlasses = useSelector(
    (state) => state.filter.hasWineGlasses,
  );
  const filterPrice = useSelector((state) => state.filter.price);
  const filterOpenNow = useSelector((state) => state.filter.openNow);

  const hoursOfOperation = props.markerData.hoursOfOperation ? convertHoursOfOperation(props.markerData.hoursOfOperation) : null;

  const markerId = props.markerData.placeId;
  function isActiveMarker() {
    return activeMarkerId === markerId;
  }

  const handleClick = () => {
    if (isActiveMarker()) {
      dispatch(setActiveMarkerId(null));
    } else {
      dispatch(setActiveMarkerId(markerId));
    }
  };

  // check if this current item is the same as the highlighted_placeId
  const [hovered, setHovered] = useState(false);
  const highlighted_place_id = useSelector(
    (state) => state.place.highlighted_place_id,
  );
  const isHighlighted = hovered || (props.markerData.placeId === highlighted_place_id);

  /**
   * z-index of active marker should be higher than all other markers
   *
   * the reason this needs to be set on 3 layers above has to do with stacking contexts.
   * whenever a transform/opacity/translate
   */
  useEffect(() => {
    const getBaseMarker = () => {
      return myElement.current.parentElement.parentElement;
    };
    if (activeMarkerId === markerId) {
      getBaseMarker().style.zIndex = 10;
    } else {
      getBaseMarker().style.zIndex = null;
    }
  }, [activeMarkerId, markerId]);

  function thisIsOpenNow(placeData) {
    if (filterOpenNow === null) { // filter is null means everything should be shown
      return true;
    } else {
      if (!("hoursOfOperation" in placeData)) { // if hoursOfOperation not available, don't show
        return false;
      } else {
        return isOpenNow(placeData.hoursOfOperation);
      }
    }
  }

  const isVisible = () => {
    let reservations =
      props.markerData.acceptsReservations === filterAcceptsReservations ||
      filterAcceptsReservations === null;
    let glasses =
      props.markerData.hasWineGlasses === filterHasWineGlasses ||
      filterHasWineGlasses === null;
    let belowPriceFilter =
      filterPrice === null || props.markerData.corkageFee <= filterPrice;
    let openNowFilter =
      filterOpenNow === null || thisIsOpenNow(props.markerData);
    if (reservations && glasses && belowPriceFilter && openNowFilter) {
      return true;
    }
    return false;
  };

  return (
    <Box
      ref={myElement}
      className={`pin ${!isVisible() ? "hide" : ""}`}
      onClick={handleClick}
      onMouseEnter={() =>
        dispatch(setHighlightedPlaceId(props.markerData.placeId))
      }
      onMouseLeave={() => dispatch(setHighlightedPlaceId(null))}
      sx={{
        display: isVisible() ? "flex" : "none",
        justifyContent: "center",
        alignItems: "center",
        transition: "all 0.3s east-out",
        position: "relative",
      }}
    >
      {/* <div className={`pin__icon ${isHighlighted ? 'highlighted' : ''}`}> */}
      <Box
        className="pin__icon"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: COLOR_WHITE,
          fontWeight: "bold",
          fontSize: 12,
          backgroundColor: isHighlighted ? COLOR_PRIMARY : COLOR_BLACK,
          padding: "3px",
          borderRadius: "25%",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            left: "50%",
            top: "100%",
            transform: "translate(-50%, 0)",
            width: 0,
            height: 0,
            borderLeft: "4px solid transparent",
            borderRight: "4px solid transparent",
            borderTop: `4px solid ${isHighlighted ? COLOR_PRIMARY : COLOR_BLACK}`,
          },
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {props.markerData.corkageFee === 0
          ? "Free"
          : "$" + props.markerData.corkageFee}
      </Box>
      <Box
        className={`pin__details ${isActiveMarker() ? "highlight" : ""}`}
        onClick={() => {
          navigate(`/place/${props.markerData.placeId}`);
          dispatch(setViewPlaceId(props.markerData.placeId));
          dispatch(setShowViewPlaceModal(true));
        }}
        sx={{
          minWidth: "max-content",
          maxWidth: "300px",
          boxShadow: "10px 10px 5px rgba(0, 0, 0, 0.2)",
          backgroundColor: COLOR_WHITE,
          borderRadius: "8px",
          fontSize: "14px",
          padding: "20px 20px",
          position: "absolute",
          bottom: "150%",
          opacity: isActiveMarker() ? "1" : "0",
          visibility: isActiveMarker() ? "visible" : "hidden",
          transition: "opacity 0.3s ease-out, visibility 0.3s ease-out",
        }}
      >
        <Stack direction="row" spacing={1} sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}>
          <Stack>
            <Stack direction="row">
              <Typography level="h4" fontWeight="lg">{props.markerData.name}</Typography>
            </Stack>
            {hoursOfOperation && (
              <Stack direction="row" spacing={0.8} sx={{
                justifyContent: "flex-start",
              }}>
                <OpenNow isOpen={isOpenNow(props.markerData.hoursOfOperation)} />
                <Typography level="body-sm">•</Typography>
                <Typography level="body-sm">{getDayOfTheWeek()}</Typography>
                <Typography level="body-sm">{hoursOfOperation[getDayOfTheWeek()].join(", ")}</Typography>
              </Stack>
            )}
            <Stack direction="row" spacing={0.5}>
              <Typography level="body-sm" sx={{ fontWeight: 900 }}>${props.markerData.corkageFee}</Typography>
              <Typography level="body-sm">corkage fee</Typography>
              {props.markerData.bottleLimit && (
                <>
                  <Typography level="body-sm">•</Typography>
                  <Typography level="body-sm">limit {props.markerData.bottleLimit} bottles</Typography>
                </>
              )}
            </Stack>
          </Stack>
          <Stack>
            <IconButton size="xs"
              onClick={() => {
                if (username === null) {
                  alert("please create a user account");
                } else {
                  dispatch(setEditPlaceId(props.markerData.placeId));
                  dispatch(setShowEditPlaceModal(true));
                }
              }}
            >
              <EditIcon></EditIcon>
            </IconButton>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

export default MapMarker;
